import type { FrameMetrics } from "./camera/frame-metrics"
import { focusIssue, lightIssue } from "./camera/frame-metrics"
import type { PhoneCheck, PlannedShot, ShotRecord, ShotVerdict } from "./types"

// What the screen says about a photo that was just taken, combining the
// instant on-device checks (light / focus, and the car-view brain: which view
// of the car it is) with the server's AI check when it lands. On-device
// answers win: they're immediate, and they work offline.
// Pure; shared by the phone-camera walk and the live camera's review strip.

export type ResultState =
  | { kind: "checking" }
  // `phone`: the brain recognised the asked view (said back in words).
  | { kind: "good"; phone: PhoneCheck | null }
  | { kind: "issue"; verdict: Exclude<ShotVerdict, "good" | "wrong-side"> }
  // `phone` set: the brain is sure this is another corner (or the same spot
  // as before). Null: the server isn't sure, so it asks.
  | { kind: "wrong-side"; phone: PhoneCheck | null }
  | { kind: "queued" }
  | { kind: "failed" }

// The phone's verdict, when it's a reason to look again.
function phoneProblem(r: ShotRecord): ShotVerdict | null {
  if (r.accepted || !r.phone) return null
  switch (r.phone.verdict) {
    case "wrong-corner":
    case "same-spot":
      return "wrong-side"
    case "too-close":
      return "too-close"
    case "too-far":
      return "too-far"
    default:
      return null
  }
}

// The phone is sure the photo shows the asked view: a server guess that it's
// another side doesn't get to nag (the phone's call is the measured one,
// scripts/car-brain/REPORT.md).
export const PHONE_SURE = 0.8
function phoneConfirms(r: ShotRecord): boolean {
  return r.phone?.verdict === "ok" && r.phone.confidence >= PHONE_SURE
}

// What Review shows for a photo: the phone's verdict first, else the server's.
export function effectiveVerdict(r: ShotRecord): ShotVerdict | null {
  const phone = phoneProblem(r)
  if (phone) return phone
  if (r.verdict === "wrong-side" && phoneConfirms(r)) return "good"
  return r.verdict
}

export function localIssue(
  metrics: FrameMetrics | null,
  shot: PlannedShot | null,
): "dark" | "blurry" | null {
  if (!metrics) return null
  if (lightIssue(metrics)) return "dark"
  // Close-ups of plain paint have little texture — only the absolute floor.
  if (focusIssue(metrics) && shot?.kind !== "sticker") return "blurry"
  return null
}

export function resultFor(record: ShotRecord | undefined, local: "dark" | "blurry" | null): ResultState {
  if (!record) return { kind: "checking" }
  if (local && !record.accepted) return { kind: "issue", verdict: local }
  const phone = phoneProblem(record)
  if (phone === "wrong-side") return { kind: "wrong-side", phone: record.phone ?? null }
  if (phone === "too-close" || phone === "too-far") return { kind: "issue", verdict: phone }
  if (record.status === "queued") return { kind: "queued" }
  if (record.status === "failed") return { kind: "failed" }
  if (record.phone === undefined || !record.verdict) return { kind: "checking" }
  const recognised = record.phone?.verdict === "ok" ? record.phone : null
  if (record.accepted || record.verdict === "good") return { kind: "good", phone: recognised }
  if (record.verdict === "wrong-side") {
    return phoneConfirms(record) ? { kind: "good", phone: recognised } : { kind: "wrong-side", phone: null }
  }
  return { kind: "issue", verdict: record.verdict }
}
